<template>
  <div class="asignar-boleto-wrapper">
    <div class="asignar-boleto-card">
      <!-- Cargando -->
      <div v-if="cargando" class="estado-center">
        <div class="spinner-border text-primary mb-3" role="status"></div>
        <p class="text-muted">Verificando disponibilidad...</p>
      </div>

      <!-- Link inválido -->
      <div v-else-if="linkInvalido" class="estado-center">
        <div class="estado-icon">❌</div>
        <h4>Link inválido</h4>
        <p class="text-muted">
          Este enlace no existe o ya expiró. Contacta a quien te compartió el boleto.
        </p>
      </div>

      <!-- Todos los boletos ya registrados -->
      <div v-else-if="completo" class="estado-center">
        <div class="estado-icon">✅</div>
        <h4>Registro completo</h4>
        <p class="text-muted">
          Todos los boletos de esta compra ya han sido registrados.
        </p>
      </div>

      <!-- Registro exitoso -->
      <div v-else-if="registrado" class="estado-center">
        <div class="estado-icon">{{ esStreaming ? "▶️" : "🎟️" }}</div>
        <h4>¡Registro exitoso!</h4>
        <p v-if="esStreaming">
          En breve recibirás tus credenciales de acceso en
          <strong>{{ boleto.email }}</strong
          >.
        </p>
        <p v-else>
          En breve recibirás tu código QR en <strong>{{ boleto.email }}</strong
          >.
        </p>
        <p class="text-muted" style="font-size: 0.85rem">
          Revisa tu bandeja de entrada y correo no deseado.
        </p>
      </div>

      <!-- Términos de privacidad -->
      <div v-else-if="viendoTerminos">
        <TerminosDePrivacidad />
        <div class="text-end mt-3">
          <button class="btn btn-outline-primary" @click="viendoTerminos = false">
            ← Volver al formulario
          </button>
        </div>
      </div>

      <!-- Formulario -->
      <form v-else @submit.prevent="enviar">
        <h3 class="form-titulo">
          {{ esStreaming ? "Registro de acceso streaming" : "Registro de boleto" }}
        </h3>
        <p class="form-subtitulo">
          {{ nombreEvento }} · <strong>{{ pendientes }}</strong> lugar{{
            pendientes === 1 ? "" : "es"
          }}
          disponible{{ pendientes === 1 ? "" : "s" }}
        </p>

        <!-- Nombre -->
        <div class="mb-3">
          <label class="form-label">Nombre completo *</label>
          <input
            v-model="boleto.nombre"
            type="text"
            class="form-control"
            placeholder="Como aparece en tu identificación"
            required
          />
        </div>

        <!-- Correo -->
        <div class="mb-3">
          <label class="form-label">Correo electrónico *</label>
          <input
            v-model="boleto.email"
            type="email"
            class="form-control"
            placeholder="Aquí recibirás tu QR"
            required
          />
        </div>

        <!-- Fecha de nacimiento -->
        <div class="mb-3">
          <label class="form-label">Fecha de nacimiento *</label>
          <input
            v-model="boleto.fechaNacimiento"
            @input="formatearFecha"
            type="text"
            class="form-control"
            placeholder="dd/mm/aaaa"
            maxlength="10"
            required
          />
        </div>

        <!-- Foto — solo si es menor de edad Y es boleto presencial -->
        <transition name="fade">
          <div v-if="esMenor && !esStreaming" class="mb-3 foto-menor-box">
            <p class="foto-menor-label">
              👶 El asistente es menor de edad — sube una foto para validar su acceso en
              la entrada.
            </p>
            <div class="foto-uploader" @click="inputImage?.click()">
              <img
                :src="imagePreview || avatarDefault"
                alt="Foto del asistente"
                class="foto-preview"
              />
              <span class="foto-edit-icon">✎</span>
            </div>
            <input
              ref="inputImage"
              type="file"
              class="d-none"
              accept="image/*"
              @change="handleImageChange"
            />
            <small class="text-muted d-block mt-2 text-center">
              La imagen se eliminará 12 horas después del evento.
            </small>
          </div>
        </transition>

        <!-- Términos -->
        <div class="mb-4 text-center">
          <small class="text-muted">
            Al registrarte aceptas nuestros
            <a href="#" @click.prevent="viendoTerminos = true">términos de privacidad</a>.
          </small>
        </div>

        <!-- Error -->
        <div
          v-if="errorMsg"
          class="alert alert-danger py-2 mb-3"
          style="font-size: 0.9rem"
        >
          {{ errorMsg }}
        </div>

        <div class="d-grid">
          <button class="btn btn-primary btn-lg" type="submit" :disabled="enviando">
            <span v-if="enviando" class="spinner-border spinner-border-sm me-2"></span>
            {{ enviando ? "Registrando..." : "Confirmar registro" }}
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from "vue";
import avatarDefault from "@/assets/img/Evolve/usuario.png";
import TerminosDePrivacidad from "@/components/Evento-Julio/Boletos/TerminosDePrivacidad.vue";
import { getDetalleInvitado, registrarInvitado } from "@/utils/api_evento";

const props = defineProps({
  token: { type: String, required: true },
});

// ── Estado de pantalla ────────────────────────────────────────────────────────
const cargando = ref(true);
const linkInvalido = ref(false);
const completo = ref(false);
const registrado = ref(false);
const enviando = ref(false);
const viendoTerminos = ref(false);
const errorMsg = ref("");

// ── Datos del evento ──────────────────────────────────────────────────────────
const nombreEvento = ref("");
const pendientes = ref(0);
const esStreaming = ref(false);

// ── Formulario ────────────────────────────────────────────────────────────────
const boleto = ref({ nombre: "", email: "", fechaNacimiento: "", foto: null });
const imagePreview = ref(null);
const inputImage = ref(null);

// ── Detectar menor de edad ────────────────────────────────────────────────────
const esMenor = computed(() => {
  const partes = boleto.value.fechaNacimiento.split("/");
  if (partes.length !== 3) return false;
  const [dd, mm, yyyy] = partes.map(Number);
  if (!dd || !mm || !yyyy || yyyy < 1900) return false;
  const nacimiento = new Date(yyyy, mm - 1, dd);
  const hoy = new Date();
  let edad = hoy.getFullYear() - nacimiento.getFullYear();
  if (
    hoy.getMonth() < nacimiento.getMonth() ||
    (hoy.getMonth() === nacimiento.getMonth() && hoy.getDate() < nacimiento.getDate())
  )
    edad--;
  return edad < 18;
});

// ── Al montar ─────────────────────────────────────────────────────────────────
onMounted(async () => {
  try {
    const data = await getDetalleInvitado(props.token);
    if (data === null) {
      linkInvalido.value = true;
      return;
    }
    if (data.completo) {
      completo.value = true;
      return;
    }
    nombreEvento.value = data.nombre_evento;
    pendientes.value = data.pendientes;
    esStreaming.value = data.es_streaming || false;
  } catch (e) {
    if (e.response?.status === 410) completo.value = true;
    else linkInvalido.value = true;
  } finally {
    cargando.value = false;
  }
});

// ── Imagen ────────────────────────────────────────────────────────────────────
function handleImageChange(e) {
  const file = e.target.files[0];
  if (file && file.type.startsWith("image/")) {
    boleto.value.foto = file;
    imagePreview.value = URL.createObjectURL(file);
    errorMsg.value = "";
  } else {
    boleto.value.foto = null;
    imagePreview.value = null;
    errorMsg.value = "Por favor selecciona una imagen válida.";
  }
}

// ── Formatear fecha dd/mm/aaaa ────────────────────────────────────────────────
function formatearFecha(e) {
  let val = e.target.value.replace(/\D/g, "").slice(0, 8);
  if (val.length >= 5) val = val.slice(0, 2) + "/" + val.slice(2, 4) + "/" + val.slice(4);
  else if (val.length >= 3) val = val.slice(0, 2) + "/" + val.slice(2);
  boleto.value.fechaNacimiento = val;
}

// ── Enviar ────────────────────────────────────────────────────────────────────
async function enviar() {
  errorMsg.value = "";

  if (esMenor.value && !esStreaming.value && !boleto.value.foto) {
    errorMsg.value = "La foto es obligatoria para menores de edad.";
    return;
  }

  enviando.value = true;
  try {
    const formData = new FormData();
    formData.append("nombre_asistente", boleto.value.nombre);
    formData.append("email_asistente", boleto.value.email);
    formData.append("fecha_nacimiento", boleto.value.fechaNacimiento); // dd/mm/aaaa — el backend lo convierte
    formData.append("es_menor", esMenor.value ? "true" : "false");
    if (boleto.value.foto && !esStreaming.value)
      formData.append("foto_asistente", boleto.value.foto);

    await registrarInvitado(props.token, formData);
    registrado.value = true;
  } catch (e) {
    if (e.response?.status === 410) {
      completo.value = true;
      return;
    }

    const data = e.response?.data;
    if (data?.fecha_nacimiento) errorMsg.value = data.fecha_nacimiento[0];
    else if (data?.email_asistente) errorMsg.value = data.email_asistente[0];
    else if (data?.detail) errorMsg.value = data.detail;
    else errorMsg.value = "No fue posible completar el registro. Intenta de nuevo.";
  } finally {
    enviando.value = false;
  }
}
</script>

<style scoped>
.asignar-boleto-wrapper {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: url("@/assets/img/Evolve/img_7.jpg") center center / cover no-repeat;
  padding: 2rem;
}

.asignar-boleto-card {
  background: #fff;
  border-radius: 20px;
  padding: 2.5rem 2rem;
  max-width: 480px;
  width: 100%;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.15);
}

/* Estados */
.estado-center {
  text-align: center;
  padding: 1rem 0;
}
.estado-icon {
  font-size: 3rem;
  margin-bottom: 1rem;
}

/* Formulario */
.form-titulo {
  text-align: center;
  font-weight: 700;
  margin-bottom: 0.25rem;
}
.form-subtitulo {
  text-align: center;
  color: #666;
  font-size: 0.9rem;
  margin-bottom: 1.75rem;
}

/* Foto menor */
.foto-menor-box {
  background: #f0f4ff;
  border: 1px solid #c8d8f8;
  border-radius: 12px;
  padding: 1.25rem;
  text-align: center;
}
.foto-menor-label {
  font-size: 0.88rem;
  color: #444;
  margin-bottom: 1rem;
}
.foto-uploader {
  position: relative;
  display: inline-block;
  cursor: pointer;
}
.foto-preview {
  width: 120px;
  height: 120px;
  border-radius: 50%;
  object-fit: cover;
  border: 3px solid #aac4f5;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
}
.foto-edit-icon {
  position: absolute;
  bottom: 4px;
  right: 4px;
  background: #fff;
  border-radius: 50%;
  padding: 3px 6px;
  font-size: 13px;
  border: 1px solid #ccc;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.15);
}

/* Transición foto */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s, transform 0.3s;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
  transform: translateY(-6px);
}
</style>
